import { useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import {
  CrewComparisonData,
  CrewComparisonStrikeStatus,
} from "src/store/api/generatedApi";
import { STRIKE_COLORS } from "components/common/plots/colorSets";

type HeatStatusesChartProps = Readonly<{
  data: CrewComparisonData;
}>;

const STRIKE_ORDER: CrewComparisonStrikeStatus["status"][] = [
  "red",
  "amber",
  "green",
];

export const HeatStatusesChart = ({ data }: HeatStatusesChartProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  const { plotData, hasCrewData } = useMemo(() => {
    const allCrew = data.crews.find((c) => c.crew_id === "All");
    const otherCrews = data.crews.filter((c) => c.crew_id !== "All");
    const crewIds = otherCrews.map((c) => c.crew_id);
    // Add an empty placeholder on the left of the graph
    // so that the crew bars line up with the materials chart
    const xLabels = ["", t("allCrews"), ...crewIds];

    const traces = STRIKE_ORDER.map((strikeType) => {
      const allCrewStrike = allCrew?.strike_statuses.find(
        (s) => s.status === strikeType
      );
      const allCrewPercentage = allCrewStrike
        ? allCrewStrike.fraction * 100
        : 0;

      const crewPercentages = otherCrews.map((crew) => {
        const strike = crew.strike_statuses.find(
          (s) => s.status === strikeType
        );
        return strike ? strike.fraction * 100 : 0;
      });

      return {
        x: xLabels,
        y: [0, allCrewPercentage, ...crewPercentages],
        name: t(strikeType),
        type: "bar" as const,
        marker: {
          color: STRIKE_COLORS[strikeType],
        },
        hovertemplate: `%{x}<br>${t(strikeType)}: %{y:.1f}%<extra></extra>`,
      };
    });

    return { plotData: traces, hasCrewData: otherCrews.length > 0 };
  }, [data, t]);

  return (
    <Plot
      data={plotData}
      layout={{
        autosize: true,
        barmode: "stack",
        xaxis: {
          title: { text: t("crewId") },
          type: "category",
        },
        yaxis: {
          title: { text: t("proportionOfHeats") },
          ticksuffix: "%",
          range: [0, 100],
        },
        margin: {
          l: 70,
          r: 120,
          t: 40,
          b: 60,
        },
        legend: {
          x: 1.02,
          y: 1,
          xanchor: "left",
          title: { text: t("heatStatus") },
        },
        hovermode: "x unified",
        shapes: hasCrewData
          ? [
              {
                type: "line",
                x0: 1.5,
                x1: 1.5,
                y0: 0,
                y1: 100,
                line: {
                  color: theme.palette.grey[500],
                  width: 1.5,
                  dash: "dot",
                },
              },
            ]
          : [],
      }}
      config={{
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
      }}
      style={{ width: "100%", height: "400px" }}
      useResizeHandler
    />
  );
};
